import * as vp from 'vopay';

import { FunctionComponent, useContext, useMemo } from 'react';
import UsersContext from './UsersContext';

import { DialogueHandler, UserDialogueHandlers } from '../types';

const { ConfirmationDialogue } = vp;

type UsersDialogueProps = {
  handleDialogue: DialogueHandler<UserDialogueHandlers>;
};

type FC = FunctionComponent<UsersDialogueProps>;

const UsersDialogue: FC = ({ handleDialogue }: UsersDialogueProps) => {
  const {
    showUserDeleteSuccessAlert,
    showUserDeleteErrorAlert,
    userDeleteError,
    showUserCreateSuccessAlert,
    showUserCreateErrorAlert,
    userCreateError,
    showUserChangePasswordSuccessAlert,
    showUserChangePasswordErrorAlert,
    userChangePasswordError,
    showUserChangePasswordConfirmationAlert,
    showUserDeleteConfirmationAlert,
    accountUser,
  } = useContext(UsersContext);

  return useMemo(
    () => (
      <div>
        {/* Delete User Error */}

        <ConfirmationDialogue
          isVisible={showUserDeleteErrorAlert}
          title="Error! The user was not successful removed."
          status="error"
          subTitle={`${userDeleteError}`}
          confirm={{
            label: 'Back to Accounts',
            action: () => handleDialogue({ type: 'remove', state: 'fail', hasNavigation: true }),
          }}
          cancel={{
            label: 'Stay Here',
            action: () => handleDialogue({ type: 'remove', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* Delete User Success */}

        <ConfirmationDialogue
          isVisible={showUserDeleteSuccessAlert}
          status="success"
          title="Success! The user has been removed."
          subTitle="Navigate back to accounts or refresh your users."
          confirm={{
            label: 'Back to Accounts',
            action: () => handleDialogue({ type: 'remove', state: 'success', hasNavigation: true }),
          }}
          cancel={{
            label: 'Stay Here',
            action: () => handleDialogue({ type: 'remove', state: 'success', action: 'refresh', hasNavigation: false }),
          }}
        />

        {/* New User Error */}

        <ConfirmationDialogue
          isVisible={showUserCreateErrorAlert}
          status="error"
          title="Error! The user was not successfully created."
          subTitle={`${userCreateError}`}
          confirm={{
            label: 'Back to Accounts',
            action: () => handleDialogue({ type: 'create', state: 'fail', hasNavigation: true }),
          }}
          cancel={{
            label: 'Stay Here',
            action: () => handleDialogue({ type: 'create', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* New User Success */}

        <ConfirmationDialogue
          isVisible={showUserCreateSuccessAlert}
          title="New User Added!"
          subTitle="You have successfully added a new user. The account credentials have been sent to the email address provided."
          status="success"
          confirm={{
            label: 'OK',
            action: () => handleDialogue({ type: 'create', state: 'success', action: 'refresh', hasNavigation: false }),
          }}
        />

        {/* Password Reset Error */}

        <ConfirmationDialogue
          isVisible={showUserChangePasswordErrorAlert}
          title="Error! The password was not successfully changed."
          subTitle={`${userChangePasswordError}`}
          status="error"
          confirm={{
            label: 'Back to Accounts',
            action: () => handleDialogue({ type: 'change', state: 'fail', hasNavigation: true }),
          }}
          cancel={{
            label: 'Stay Here',
            action: () => handleDialogue({ type: 'change', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* Password Reset Success */}

        <ConfirmationDialogue
          isVisible={showUserChangePasswordSuccessAlert}
          title="Password reset successful!"
          subTitle="The new password credentials have been emailed to the account holder. Please notify the account holder of these changes. If this was made in error, contact your account manager. "
          status="success"
          confirm={{
            label: 'OK',
            action: () => handleDialogue({ type: 'change', state: 'success', action: 'refresh', hasNavigation: false }),
          }}
        />

        {/* User Delete Confirmation */}

        <ConfirmationDialogue
          isVisible={showUserDeleteConfirmationAlert}
          title="Are you sure you want to delete the following user?"
          subTitle="This action cannot be undone."
          subInfo={accountUser}
          status="alert"
          confirm={{
            label: 'Yes',
            action: () => handleDialogue({ type: 'alertDelete', state: 'success', hasNavigation: false }),
          }}
          cancel={{
            label: 'No',
            action: () => handleDialogue({ type: 'alertDelete', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* Change Password Confirmation */}

        <ConfirmationDialogue
          isVisible={showUserChangePasswordConfirmationAlert}
          title="Are you sure you want to change the password for this user account?"
          subTitle=""
          subInfo={accountUser}
          status="alert"
          confirm={{
            label: 'Yes',
            action: () => handleDialogue({ type: 'alertPassword', state: 'success', hasNavigation: false }),
          }}
          cancel={{
            label: 'No',
            action: () => handleDialogue({ type: 'alertPassword', state: 'fail', hasNavigation: false }),
          }}
        />
      </div>
    ),
    [
      showUserDeleteSuccessAlert,
      showUserDeleteErrorAlert,
      showUserCreateErrorAlert,
      showUserCreateSuccessAlert,
      showUserChangePasswordErrorAlert,
      showUserChangePasswordSuccessAlert,
      showUserDeleteConfirmationAlert,
      showUserChangePasswordConfirmationAlert,
      accountUser,
    ],
  );
};

export default UsersDialogue;
